import {
    createOverlay,
    OVERLAY_ALIGNMENT_CENTER,
} from '@vfde-brix/ws10/overlay';
import {
    OVERLAY_BUTTON_CONTAINER,
    OVERLAY_CONTAINER,
    OVERLAY_HEADLINE_CONTAINER,
    OVERLAY_IMAGE_CONTAINER,
    OVERLAY_PRICE_CONTAINER,
    OVERLAY_TEXT_CONTAINER,
} from '../container/App/constants';
import { startAppListening } from '../container/App/listener';
import {
    closeOverlay,
    fetchOverlay,
    injectOverlayContent,
    openOverlay,
    selectOverlayContextForUrl,
} from '@vfde-sails/overlay';
import { useDispatch } from '../store';
import { selectProduct } from '../container/App/selectors';
import {
    PriceType,
    UpsellFacetValue,
} from '@vfde-sails/glados-v2';
import {
    createComponents,
    NO_PATTERN_BUSINESS_LOGIC,
    Price,
} from '@vfde-brix/ws10/core';
import {
    ACCORDION_BASE_CLASSNAME,
    createAccordion,
} from '@vfde-brix/ws10/accordion';
import mountImage from './Image';
import { IMAGE_WIDTH_AUTO } from '@vfde-brix/ws10/image';
import mountHeadline from './Headline';
import { ADDITIONAL_PAGE_OPTIONS } from '@vfde-sails/constants';
import { getPriceForProduct } from '../container/App/helpers/priceHelper';
import mountPromoPrice from './PromoPrice';
import { PromoPriceAlign } from '@vfde-brix/ws10/promo-price';
import mountText from './Text';
import mountButton from './Button';
import { BUTTON_ICON_POSITION_LEFT } from '@vfde-brix/ws10/button';
import {
    selectError,
    selectSelectedProducts,
    toggleProduct,
} from '../container/App/slice';
import {
    TRACKING_INFORMATION_ACTION_REVEAL,
    TrackingInformationTrigger,
    TrackingInformationUiType,
    trackIt,
    TrackType,
} from '@vfde-sails/tracking';

/**
 * Overlay
 */
export default function mountOverlay () {
    const dispatch = useDispatch();

    let container = document.getElementById(OVERLAY_CONTAINER);

    if (!container) {
        container = document.createElement('div');
        container.id = OVERLAY_CONTAINER;

        document.body.appendChild(container);
    }

    const overlay = createOverlay(container, {
        optPaddingLayout: true,
        optContentAlign: OVERLAY_ALIGNMENT_CENTER,
        business: {
            onOpen: () => {
                dispatch(openOverlay());
            },
            onClose: () => {
                dispatch(closeOverlay());
            },
            onInject: () => {
                dispatch(injectOverlayContent());
            },
        },
    });

    startAppListening({
        actionCreator: fetchOverlay.fulfilled,
        effect: async (action, api) => {
            api.cancelActiveListeners();

            if (action.payload.content) {
                overlay.injectContent(action.payload.content);
            }
        },
    });

    startAppListening({
        actionCreator: injectOverlayContent.fulfilled,
        effect: async (_action, api) => {
            api.cancelActiveListeners();

            const context = selectOverlayContextForUrl(api.getState());
            const product = selectProduct(context?.id)(api.getState());

            if (!product) {
                return;
            }

            const selectedProducts = selectSelectedProducts(api.getState());

            createComponents(`.${ACCORDION_BASE_CLASSNAME}`, createAccordion, NO_PATTERN_BUSINESS_LOGIC, overlay.getOverlayContentContainer());

            const image = mountImage(OVERLAY_IMAGE_CONTAINER, NO_PATTERN_BUSINESS_LOGIC);

            if ((product.imageUrls.length || 0) > 0) {
                image && image.update({
                    imgSrcDesktop: product.imageUrls[0],
                    imgSrcMobile: product.imageUrls[0],
                    stdAltText: product.label,
                    stdTitle: product.label,
                    optWidth: IMAGE_WIDTH_AUTO,
                });
            }

            const headline = mountHeadline(OVERLAY_HEADLINE_CONTAINER, NO_PATTERN_BUSINESS_LOGIC);
            headline && headline.update({ stdContent: product.label });

            const { promoPrice, button: buttonProperties } = window[ADDITIONAL_PAGE_OPTIONS].featureCard;
            const { label: { [PriceType.Onetime]: oneTimePriceLabel, [PriceType.Monthly]: monthlyPriceLabel } } = promoPrice;

            const price = getPriceForProduct(product.price, product.category);
            const priceComponent = mountPromoPrice(OVERLAY_PRICE_CONTAINER, NO_PATTERN_BUSINESS_LOGIC);
            priceComponent && price && priceComponent.update({
                price: Price.fromNumber(price.value || 0),
                optAlign: PromoPriceAlign.Center,
                stdConditionAfter: price.type === PriceType.Onetime ? oneTimePriceLabel : monthlyPriceLabel,
            });

            const description = mountText(OVERLAY_TEXT_CONTAINER, NO_PATTERN_BUSINESS_LOGIC);

            if (product.category.value === UpsellFacetValue.Accessory && product.description?.length > 0 && description) {
                description.update({ content: `<ul class="ws10-hook-list"><li>${product.description.join('</li><li>')}</li></ul>` });
            }

            const button = mountButton(OVERLAY_BUTTON_CONTAINER, {
                onClick: (event: Event) => {
                    event.preventDefault();
                    dispatch(toggleProduct(product));
                    overlay.close();
                },
            });

            const isSelected = selectedProducts.some(selectedProduct => selectedProduct.id === context?.id);
            const icon = {
                optPosition: BUTTON_ICON_POSITION_LEFT,
                stdIconName: 'tick',
            };
            const { unselected, selected, unselectable } = buttonProperties;
            const disableButton = !!selectError(api.getState())
                && !selectedProducts.some(selectedProduct => selectedProduct.id === context?.id);
            button && button.update({
                stdContent: disableButton ? unselectable : isSelected ? selected : unselected,
                optAriaChecked: isSelected,
                optDisabled: disableButton,
                optRole: 'checkbox',
                ...(isSelected && { icon }),
            });

            overlay.open();
        },
    });

    startAppListening({
        actionCreator: openOverlay,
        effect: (_action, api) => {
            const context = selectOverlayContextForUrl(api.getState());

            if (context?.trackingData) {
                trackIt(TrackType.Information, {
                    /* eslint-disable camelcase, @typescript-eslint/naming-convention */
                    information_action: TRACKING_INFORMATION_ACTION_REVEAL,
                    information_name: context.trackingData.trackingName,
                    information_ui_type: TrackingInformationUiType.Overlay,
                    information_trigger: TrackingInformationTrigger.Anchor,
                    /* eslint-enable camelcase, @typescript-eslint/naming-convention */
                });
            }
        },
    });

    return overlay;
}
